import React, { useMemo } from 'react';
import { Collapse, IconButton, TableCell, TableRow, Tooltip, useTheme } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { IPrintFileLineItem, getCurrentJob, getPrintFileUrlSource } from '../../api/printFileJobs';
import {
    collectLineItemImages,
    countJobsByStatus,
    fmtDateTime,
    ILightboxImage,
    JOB_STATUS,
    JOB_STATUS_ORDER,
    PRINT_FILE_STATUS,
    REVIEW_RESULT,
    styleFor,
} from '../../lib/printFileJobs/ui';
import PrintFileLineItemDetails, { ImageThumb, StatusBadge } from './PrintFileLineItemDetails';
import { useStyles } from './styles';

export const PRINT_FILE_JOBS_COLUMNS = 8;

interface Props {
    item: IPrintFileLineItem;
    expanded: boolean;
    onToggle: () => void;
    onOpenImage: (images: ILightboxImage[], index: number) => void;
}

const SOURCE_SHORT: Record<string, string> = {
    final: 'final',
    draft: 'draft',
    preprocessed: 'preproc.',
};

export default function PrintFileJobsRow({ item, expanded, onToggle, onOpenImage }: Props) {
    const classes = useStyles();
    const theme = useTheme();
    const pf = item.printFile;
    const current = getCurrentJob(item);
    const source = getPrintFileUrlSource(item);
    const images = useMemo(() => collectLineItemImages(item), [item]);
    const counts = useMemo(() => countJobsByStatus(item.jobs || []), [item.jobs]);

    const openByUrl = (url: string, title?: string) => {
        let idx = title ? images.findIndex((im) => im.url === url && im.title === title) : -1;
        if (idx < 0) idx = images.findIndex((im) => im.url === url);
        if (idx >= 0) onOpenImage(images, idx);
    };

    const currentStyle = current ? styleFor(JOB_STATUS, current.status) : null;
    const currentNote = current
        ? current.errorType || current.errorStep || current.skipReason || null
        : null;

    return (
        <>
            <TableRow
                hover
                onClick={onToggle}
                sx={{
                    cursor: 'pointer',
                    '& > td': { borderBottom: expanded ? 0 : undefined },
                    backgroundColor: expanded ? (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.025)') : undefined,
                }}
            >
                <TableCell padding="checkbox">
                    <IconButton
                        size="small"
                        aria-label={expanded ? 'Collapse' : 'Expand'}
                        aria-expanded={expanded}
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggle();
                        }}
                    >
                        {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>

                <TableCell>
                    <div style={{ fontWeight: 700 }}>{item.lineItemId}</div>
                    <div className={classes.mono} style={{ opacity: 0.65 }} title="printReadyFileId">
                        {item.printReadyFileId}
                    </div>
                </TableCell>

                <TableCell>
                    <div title="orderId">order <span className={classes.mono}>{pf?.orderId ?? '—'}</span></div>
                    <div className={classes.mono} title="sku">{pf?.sku ?? '—'}</div>
                    {pf?.color ? (
                        <div className={classes.muted}>
                            <span className={classes.colorDot} style={{ backgroundColor: pf.color === 'white' ? '#fff' : pf.color === 'black' ? '#000' : pf.color }} />
                            {pf.color}
                        </div>
                    ) : null}
                </TableCell>

                <TableCell>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                        <StatusBadge style={styleFor(PRINT_FILE_STATUS, pf?.printFileStatus)} title={`printFileStatus: ${pf?.printFileStatus}`} />
                        {pf?.reviewResult ? (
                            <StatusBadge style={styleFor(REVIEW_RESULT, pf.reviewResult)} title={`reviewResult: ${pf.reviewResult}`} />
                        ) : (
                            <span className={classes.muted}>review —</span>
                        )}
                        {pf?.hasManualEdits ? <span className={classes.muted} title="hasManualEdits">✎ manual edits</span> : null}
                    </div>
                </TableCell>

                <TableCell>
                    {current && currentStyle ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                            <StatusBadge style={currentStyle} title={`job ${current.id}`} />
                            {currentNote ? (
                                <Tooltip title={current.errorMessage || current.skipReason || ''}>
                                    <span className={classes.muted} style={{ color: current.status === 'failed' ? '#d03b3b' : undefined, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                                        {currentNote}
                                    </span>
                                </Tooltip>
                            ) : null}
                            <span className={classes.muted} title={current.finishedAt ? `finished ${current.finishedAt}` : `created ${current.createdAt}`}>
                                {fmtDateTime(current.finishedAt || current.createdAt)}
                            </span>
                        </div>
                    ) : (
                        <span className={classes.muted}>no jobs</span>
                    )}
                </TableCell>

                <TableCell align="center">
                    <div style={{ fontWeight: 600 }}>{item.jobs?.length ?? 0}</div>
                    <div style={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
                        {JOB_STATUS_ORDER.filter((s) => counts[s]).map((s) => (
                            <span
                                key={s}
                                className={classes.badge}
                                style={{ backgroundColor: JOB_STATUS[s].color, color: JOB_STATUS[s].textColor || '#fff', padding: '0 6px', fontSize: 10 }}
                                title={`${JOB_STATUS[s].label}: ${counts[s]}`}
                            >
                                {counts[s]}
                            </span>
                        ))}
                    </div>
                </TableCell>

                <TableCell>
                    <div className={classes.thumbRow}>
                        <ImageThumb url={item.originalUrl} label="orig" title="Original (Shopify)" onClick={() => item.originalUrl && openByUrl(item.originalUrl, 'Original (Shopify)')} />
                        <ImageThumb url={item.mockupUrl} label="mockup" title="Mockup (Shopify)" onClick={() => item.mockupUrl && openByUrl(item.mockupUrl, 'Mockup (Shopify)')} />
                        <ImageThumb
                            url={item.printFileUrl}
                            label={source ? SOURCE_SHORT[source] : 'print'}
                            title={`Print file (resolved${source ? ` from ${source}` : ''})`}
                            onClick={() => item.printFileUrl && openByUrl(item.printFileUrl, 'Print file (resolved)')}
                        />
                    </div>
                </TableCell>

                <TableCell style={{ whiteSpace: 'nowrap' }}>
                    <div title={`updatedAt ${pf?.updatedAt ?? ''}`}>{fmtDateTime(pf?.updatedAt)}</div>
                    <div className={classes.muted} title={`createdAt ${pf?.createdAt ?? ''}`}>created {fmtDateTime(pf?.createdAt)}</div>
                </TableCell>
            </TableRow>

            <TableRow>
                <TableCell className={classes.detailsCell} colSpan={PRINT_FILE_JOBS_COLUMNS}>
                    <Collapse in={expanded} timeout="auto" unmountOnExit>
                        <PrintFileLineItemDetails item={item} onOpenImage={openByUrl} />
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}
